import React, { Component } from 'react';
import { MdCircle } from 'react-icons/md';
import { useMapData } from '@app/contexts/mapData.context';

// Helper function to create SVG marker with shape only (no text)
const createMarker = (
  color: string,
  size: number,
  shape: 'circle' | 'square' = 'circle'
): string => {
  const diameter = size * 2;
  const radius = size;
  
  const shapeElement = shape === 'square' 
    ? `<rect x="1" y="1" width="${diameter - 2}" height="${diameter - 2}" fill="${color}" stroke="#ffffff" stroke-width="2" fill-opacity="0.8"/>`
    : `<circle cx="${radius}" cy="${radius}" r="${radius - 1}" fill="${color}" stroke="#ffffff" stroke-width="2" fill-opacity="0.8"/>`;
  
  return `
    <svg width="${diameter}" height="${diameter}" viewBox="0 0 ${diameter} ${diameter}" xmlns="http://www.w3.org/2000/svg">
      ${shapeElement}
    </svg>
  `;
};

// Convert SVG to data URL
const svgToDataUrl = (svgString: string): string => {
  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};

// Create a functional wrapper component that uses the hook
const GoogleMapWithBubblesWrapper: React.FC<{}> = () => {
  const { state, loadGoogleMaps, loadDashboardData } = useMapData();
  
  React.useEffect(() => {
    // Load Google Maps and dashboard data if not already loaded
    const loadData = async () => {
      const promises = [];
      
      // Only load Google Maps if not already loaded or loading
      if (!state.googleMapsLoaded && !state.googleMapsLoading) {
        promises.push(loadGoogleMaps());
      }
      
      // Load dashboard data if needed
      promises.push(loadDashboardData());
      
      if (promises.length > 0) {
        await Promise.all(promises);
      }
    };
    loadData();
  }, [state.googleMapsLoaded, state.googleMapsLoading, loadGoogleMaps, loadDashboardData]);

  // Pass the cached state to the class component
  return (
    <GoogleMapWithBubbles 
      googleMapsLoaded={state.googleMapsLoaded}
      googleMapsLoading={state.googleMapsLoading}
      googleMapsError={state.googleMapsError}
      controllers={state.controllers}
      controllersLoading={state.dashboardLoading}
      controllersError={state.dashboardError}
    />
  );
};

interface Controller {
  id: string;
  name?: string;
  city?: string;
  country?: string;
  state?: string;
  province?: string;
  region?: string;
  address1?: string;
  address2?: string;
  postal_code?: string;
  status?: number;
  location?: {
    coordinates?: {
      latitude: number;
      longitude: number;
    };
    city?: string;
  };
  [key: string]: any;
}

interface BubbleMapProps {
  googleMapsLoaded?: boolean;
  googleMapsLoading?: boolean;
  googleMapsError?: string | null;
  controllers?: Controller[];
  controllersLoading?: boolean;
  controllersError?: string | null;
}

interface BubbleMapState {
  mapLoaded: boolean;
  loadError: string | null;
  tooltipContent: any;
  tooltipPosition: { x: number; y: number };
}

// Status color mapping to match site list
const statusColors: Record<number, string> = {
  0: "#000000", // Unknown - Black
  1: "#ff0000", // Down - Red
  2: "#FBC02D", // Warning - Yellow
  3: "#45B649", // Good - Green
  4: "#808080"  // Missing - Gray
};

const generateRandomNumber = () => {
  const statusValues = Object.values(statusColors);
  const randomNumber = Math.floor(Math.random() * statusValues.length);
  return statusValues[randomNumber];
};

class GoogleMapWithBubbles extends Component<BubbleMapProps, BubbleMapState> {
  private mapRef = React.createRef<HTMLDivElement>();
  private map: any = null;
  private markers: any[] = [];
  private infoWindow: any = null;

  state: BubbleMapState = {
    mapLoaded: false,
    loadError: null,
    tooltipContent: null,
    tooltipPosition: { x: 0, y: 0 },
  };

  async componentDidMount() {
    // Check if Google Maps is already loaded from cache
    if (this.props.googleMapsLoaded) {
      this.initializeMap();
      this.setState({ mapLoaded: true });
    }
  }

  componentDidUpdate(prevProps: BubbleMapProps) {
    // Handle Google Maps loading state changes
    if (!prevProps.googleMapsLoaded && this.props.googleMapsLoaded) {
      this.initializeMap();
      this.setState({ mapLoaded: true });
    }

    // Handle Google Maps loading errors
    if (!prevProps.googleMapsError && this.props.googleMapsError) {
      this.setState({ loadError: this.props.googleMapsError });
    }

    // Handle controllers loading errors
    if (!prevProps.controllersError && this.props.controllersError) {
      this.setState({ loadError: this.props.controllersError });
    }

    // Update markers when controllers data changes
    if (this.state.mapLoaded && prevProps.controllers !== this.props.controllers) {
      this.updateMarkers();
    }
  }

  initializeMap = () => {
    if (!this.mapRef.current) {
      
      setTimeout(() => this.initializeMap(), 100);
      return;
    }

    if (!(window as any).google) return;

    const google = (window as any).google;
    
    // Create map with the same config as dashboard
    this.map = new google.maps.Map(this.mapRef.current, {
      zoom: 10,
      center: { lat: 30, lng: 0 },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      scaleControl: false,
      rotateControl: false,
      panControl: false,
      gestureHandling: 'auto',
      disableDefaultUI: true,
      keyboardShortcuts: false,
      clickableIcons: false,
      styles: [
        {
          "elementType": "geometry",
          "stylers": [{ "color": "#1d2c4d" }]
        },
        {
          "elementType": "labels.text.fill",
          "stylers": [{ "color": "#8ec3b9" }]
        },
        {
          "elementType": "labels.text.stroke",
          "stylers": [{ "color": "#1a3646" }]
        },
        {
          "featureType": "administrative.country",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": "#4b6878" }]
        },
        {
          "featureType": "water",
          "elementType": "geometry",
          "stylers": [{ "color": "#0e1626" }]
        },
        {
          "featureType": "road",
          "elementType": "geometry",
          "stylers": [{ "color": "#38414e" }]
        },
        {
          "featureType": "road",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": "#212a37" }]
        },
        {
          "featureType": "road",
          "elementType": "labels.text.fill",
          "stylers": [{ "color": "#9ca5b3" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "geometry",
          "stylers": [{ "color": "#746855" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "geometry.stroke",
          "stylers": [{ "color": "#1f2835" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "labels.text.fill",
          "stylers": [{ "color": "#f3d19c" }]
        },
        {
          "featureType": "road.highway",
          "elementType": "labels",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "road",
          "elementType": "labels.text",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "road",
          "elementType": "labels.icon",
          "stylers": [{ "visibility": "off" }]
        },
        // Hide all POI (Point of Interest) markers
        {
          "featureType": "poi",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.business",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.attraction",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.government",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.medical",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.park",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.place_of_worship",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.school",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "poi.sports_complex",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "transit",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "transit.line",
          "stylers": [{ "visibility": "off" }]
        },
        {
          "featureType": "transit.station",
          "stylers": [{ "visibility": "off" }]
        }
      ]
    });

    // Create info window for tooltips
    this.infoWindow = new google.maps.InfoWindow();

    // Add click listener to map to close info window when clicking outside
    this.map.addListener('click', () => {
      this.infoWindow.close();
    });

    // Add markers
    this.updateMarkers();
  };

  updateMarkers = () => {
    if (!this.map || !(window as any).google) return;

    const google = (window as any).google;

    // Clear existing markers
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];

    // Group controllers by exact coordinates lat/long
    const coordinateGroups: { [key: string]: Controller[] } = {};
    this.props.controllers
      .filter(controller => controller.location?.coordinates?.latitude && controller.location?.coordinates?.longitude) // Use coordinates from API
      .forEach(controller => {
        const coordinateKey = `${controller.location.coordinates.latitude},${controller.location.coordinates.longitude}`;
        
        if (!coordinateGroups[coordinateKey]) {
          coordinateGroups[coordinateKey] = [];
        }
        coordinateGroups[coordinateKey].push(controller);
      });

    // Create markers for each coordinate group
    Object.entries(coordinateGroups).forEach(([coordinateKey, controllers]) => {
      const firstController = controllers[0];
      
      const getMarkerColor = (controllers: Controller[]) => {
        const statuses = controllers.map(controller => controller.status || 0);
        
        // Priority order: Unknown (0), Down (1), Warning (2), Missing (4), Good (3)
        if (statuses.includes(0)) return statusColors[0]; // Unknown - Black
        if (statuses.includes(1)) return statusColors[1]; // Down - Red  
        if (statuses.includes(2)) return statusColors[2]; // Warning - Yellow
        if (statuses.includes(4)) return statusColors[4]; // Missing - Gray
        if (statuses.includes(3)) return statusColors[3]; // Good - Green
        
        // Default to Unknown if no valid status found
        return statusColors[0];
      };

      const markerColor = getMarkerColor(controllers);

      // Generate tooltip title showing controller names instead of location
      const getMarkerTitle = (controllers: Controller[]) => {
        if (controllers.length === 1) {
          // Single controller: show just the controller name
          return controllers[0].name || controllers[0].id;
        } else {
          // Multiple controllers: show first controller name + count
          const firstName = controllers[0].name || controllers[0].id;
          return `${firstName} (+${controllers.length - 1} more)`;
        }
      };

      const iconSize = Math.min(10 + controllers.length * 2, 20); // Increased base size
      const svgIcon = createMarker(markerColor, iconSize, 'square'); // Square for controllers

      const marker = new google.maps.Marker({
        position: { 
          lat: firstController.location.coordinates.latitude, 
          lng: firstController.location.coordinates.longitude 
        },
        map: this.map,
        title: `Controllers: ${getMarkerTitle(controllers)}`,
        icon: {
          url: svgToDataUrl(svgIcon),
          scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
          anchor: new google.maps.Point(iconSize, iconSize),
        }
      });

      // Add click listener for info window
      marker.addListener('click', () => {
        const content = this.createInfoWindowContent(firstController.location.city, controllers);
        this.infoWindow.setContent(content);
        this.infoWindow.open(this.map, marker);
      });

      // Add hover effects
      marker.addListener('mouseover', () => {
        const hoverSize = Math.min(12 + controllers.length * 2, 24); // Increased hover size
        const hoverIcon = createMarker(markerColor, hoverSize, 'square');
        marker.setIcon({
          url: svgToDataUrl(hoverIcon),
          scaledSize: new google.maps.Size(hoverSize * 2, hoverSize * 2),
          anchor: new google.maps.Point(hoverSize, hoverSize),
        });
      });

      marker.addListener('mouseout', () => {
        marker.setIcon({
          url: svgToDataUrl(svgIcon),
          scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
          anchor: new google.maps.Point(iconSize, iconSize),
        });
      });

      this.markers.push(marker);
    });

    // Auto-fit bounds to show all markers
    this.fitBoundsToAllMarkers();
  };

  fitBoundsToAllMarkers = () => {
    if (this.markers.length > 0) {
      const bounds = new (window as any).google.maps.LatLngBounds();
      this.markers.forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      this.map.fitBounds(bounds);
      
      // Apply the same zoom limit as in updateMarkers
      (window as any).google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if (this.map.getZoom() > 15) {
          this.map.setZoom(15);
        }
      });
    }
  };

  createInfoWindowContent = (region: string, controllers: Controller[]) => {
    const controllersHtml = controllers.map(controller => 
      `<div style="display: flex; align-items: center; margin: 2px 0;">
        <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${statusColors[controller.status || 0]}; margin-right: 5px;"></span>
        ${controller.name}
      </div>`
    ).join('');

    return `
      <div style="color: #222; font-family: Arial, sans-serif;">
        <strong>${region}</strong>
        <div style="margin-top: 5px;">
          ${controllersHtml}
        </div>
      </div>
    `;
  };

  componentWillUnmount() {
    this.cleanup();
  }

  cleanup = () => {
    if (this.markers) {
      this.markers.forEach(marker => marker.setMap(null));
      this.markers = [];
    }
    if (this.infoWindow) {
      this.infoWindow.close();
    }
  };

  render() {
    const { mapLoaded, loadError } = this.state;

    if (loadError) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          backgroundColor: '#808080',
          color: '#fff',
          fontSize: '16px'
        }}>
          {loadError}
        </div>
      );
    }

    if (!mapLoaded) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          backgroundColor: '#808080',
          color: '#fff',
          fontSize: '16px'
        }}>
          Loading Google Maps...
        </div>
      );
    }

    return (
      <div style={{ position: 'relative' }}>
        <div
          ref={this.mapRef}
          style={{
            width: '100%',
            height: '551px',
            backgroundColor: '#808080',
          }}
        />
        <style>{`
          /* Hide Terms link - not required by Google ToS */
          .gm-style a[href*="terms"] {
            display: none !important;
          }
          /* Hide Keyboard shortcuts link - not required */
          .gm-style a[href*="keyboard"] {
            display: none !important;
          }
          /* Remove background from attribution text */
          .gm-style .gmnoprint {
            background: transparent !important;
            background-color: transparent !important;
          }
          .gm-style .gm-style-cc {
            background: transparent !important;
            background-color: transparent !important;
          }
          /* Target the specific attribution container */
          .gm-style .gm-style-cc > div {
            background: transparent !important;
            background-color: transparent !important;
          }
          /* Remove any box shadow or borders */
          .gm-style .gm-style-cc * {
            background: transparent !important;
            background-color: transparent !important;
            box-shadow: none !important;
          }
        `}</style>
      </div>
    );
  }
}

export default GoogleMapWithBubblesWrapper; 